import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Store as StoreIcon,
  Bot,
  Palette,
  Activity,
  CheckCircle2,
  Sliders,
  ExternalLink,
  Sparkles,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StorePreferencesForm } from '@/features/preferences/components/StorePreferencesForm';
import { WidgetPreview } from '@/features/preferences/components/WidgetPreview';
import {
  useGetApiWorkspacesWorkspaceIdStoresStoreIdPreferences,
  usePutApiWorkspacesWorkspaceIdStoresStoreIdPreferences,
  usePatchApiWorkspacesWorkspaceIdStoresStoreIdPreferences,
  getGetApiWorkspacesWorkspaceIdStoresStoreIdPreferencesQueryKey,
} from '@/api/generated/stores/stores';
import { toast } from 'sonner';
import type { StoreItem, StorePreferences, UpdateStorePreferencesCommand } from '@/types';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface StoreDetailViewProps {
  store: StoreItem;
  onBack: () => void;
}

interface TopicSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const TOPIC_SECTIONS: TopicSection[] = [
  {
    id: 'overview',
    label: 'Visão Geral da Loja',
    icon: StoreIcon,
    description: 'Status da conexão e sincronização com a plataforma',
  },
  {
    id: 'widget-status',
    label: 'Status do Chat',
    icon: Sliders,
    description: 'Ativação e visibilidade do chat na loja virtual',
  },
  {
    id: 'ai-persona',
    label: 'Agente de IA e Saudação',
    icon: Bot,
    description: 'Nome do agente e mensagem de boas-vindas',
  },
  {
    id: 'widget-appearance',
    label: 'Visual do Chat',
    icon: Palette,
    description: 'Cores, título e estilo do chat na sua loja',
  },
];

export const StoreDetailView: React.FC<StoreDetailViewProps> = ({ store, onBack }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeSection, setActiveSection] = useState('overview');
  const { activeWorkspaceId } = useWorkspaceStore();

  // Fetch preferences for the selected store
  const {
    data: preferencesData,
    isLoading: isLoadingPreferences,
  } = useGetApiWorkspacesWorkspaceIdStoresStoreIdPreferences(activeWorkspaceId!, store.id, {
    query: {
      enabled: Boolean(store.id && activeWorkspaceId),
    },
  });

  const preferences = (preferencesData as unknown as StorePreferences) || null;

  // Local live state for real-time widget preview
  const [livePreferences, setLivePreferences] = useState<StorePreferences>({
    storeId: store.id,
    agentName: store.provider === 'shopify' ? 'Aura' : 'Nova',
    greetingMessage: 'Hello! Welcome to our store. How can I help you today?',
    widgetTitle: store.name || store.storeName || 'Customer Support',
    widgetPrimaryColor: '#2563eb',
    widgetSecondaryColor: '#64748b',
    systemPrompt: null,
    isWidgetEnabled: true,
  });

  useEffect(() => {
    if (preferences) {
      setLivePreferences({ ...preferences, storeId: store.id });
    }
  }, [preferences, store.id]);

  // Mutations
  const putMutation = usePutApiWorkspacesWorkspaceIdStoresStoreIdPreferences();
  const patchMutation = usePatchApiWorkspacesWorkspaceIdStoresStoreIdPreferences();

  const handleToggleWidgetStatus = async (enabled: boolean) => {
    if (!activeWorkspaceId) return;

    try {
      const response = await patchMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        storeId: store.id,
        data: {
          isWidgetEnabled: enabled,
        },
      });

      const updatedPrefs: StorePreferences = {
        storeId: store.id,
        agentName: (response as any)?.agentName ?? livePreferences.agentName,
        greetingMessage: (response as any)?.greetingMessage ?? livePreferences.greetingMessage,
        widgetTitle: (response as any)?.widgetTitle ?? livePreferences.widgetTitle,
        widgetPrimaryColor: (response as any)?.widgetPrimaryColor ?? livePreferences.widgetPrimaryColor,
        widgetSecondaryColor: (response as any)?.widgetSecondaryColor ?? livePreferences.widgetSecondaryColor,
        systemPrompt: (response as any)?.systemPrompt ?? livePreferences.systemPrompt,
        isWidgetEnabled: (response as any)?.isWidgetEnabled ?? enabled,
      };

      queryClient.setQueryData(
        getGetApiWorkspacesWorkspaceIdStoresStoreIdPreferencesQueryKey(activeWorkspaceId, store.id),
        updatedPrefs
      );
      setLivePreferences(updatedPrefs);

      if (enabled) {
        toast.success('Chat ativado na loja com sucesso!');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Falha ao atualizar status do chat';
      toast.error(msg);
      throw err;
    }
  };

  const handleSavePreferences = async (command: UpdateStorePreferencesCommand) => {
    if (!activeWorkspaceId) return;
    try {
      const response = await putMutation.mutateAsync({
        workspaceId: activeWorkspaceId,
        storeId: store.id,
        data: command,
      });

      const updatedPrefs: StorePreferences = {
        storeId: store.id,
        agentName: (response as any)?.agentName ?? command.agentName ?? livePreferences.agentName,
        greetingMessage: (response as any)?.greetingMessage ?? command.greetingMessage ?? livePreferences.greetingMessage,
        widgetTitle: (response as any)?.widgetTitle ?? command.widgetTitle ?? livePreferences.widgetTitle,
        widgetPrimaryColor: (response as any)?.widgetPrimaryColor ?? command.widgetPrimaryColor ?? livePreferences.widgetPrimaryColor,
        widgetSecondaryColor: (response as any)?.widgetSecondaryColor ?? command.widgetSecondaryColor ?? livePreferences.widgetSecondaryColor,
        systemPrompt: (response as any)?.systemPrompt ?? command.systemPrompt ?? livePreferences.systemPrompt,
        isWidgetEnabled: (response as any)?.isWidgetEnabled ?? command.isWidgetEnabled ?? livePreferences.isWidgetEnabled,
      };

      queryClient.setQueryData(
        getGetApiWorkspacesWorkspaceIdStoresStoreIdPreferencesQueryKey(activeWorkspaceId, store.id),
        updatedPrefs
      );
      setLivePreferences(updatedPrefs);

      toast.success('Configurações atualizadas com sucesso!');
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Falha ao atualizar preferências';
      toast.error(msg);
    }
  };

  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const elem = document.getElementById(sectionId);
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0,
      }
    );

    TOPIC_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const isConnected = store.status === 'Connected' || !store.status;

  return (
    <div className="space-y-6">
      {/* Top Header / Breadcrumb Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/80">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onBack}
            className="h-9 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Todas as Lojas</span>
          </Button>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                {store.name || store.storeName || 'Loja sem nome'}
              </h1>
              <Badge
                variant="outline"
                className="text-[11px] uppercase font-mono px-2 py-0.5 bg-primary/5 text-primary border-primary/20"
              >
                {store.provider || store.platform || 'Loja'}
              </Badge>
              <Badge
                variant="secondary"
                className="text-[11px] gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
              >
                <CheckCircle2 className="h-3 w-3" />
                {isConnected ? 'Conectada' : store.status}
              </Badge>
              {livePreferences.isWidgetEnabled === false ? (
                <Badge
                  variant="outline"
                  className="text-[11px] gap-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                >
                  Chat Pausado
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="text-[11px] gap-1 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                >
                  Chat Ativo
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Gerencie as configurações da loja, o Agente de IA e a aparência do chat para seus clientes
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/conversations?storeId=${store.id}`)}
            className="h-8 text-xs gap-1.5"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            <span>Ver Conversas da Loja</span>
          </Button>
        </div>
      </div>

      {/* Main Grid: Content Area (left) + Sticky Topic Navigation (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left / Center Content Column (8 cols) */}
        <div className="lg:col-span-8 space-y-8">
          {/* Section 1: Store Overview */}
          <section id="overview" className="scroll-mt-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground border-b border-border/60 pb-2">
              <StoreIcon className="h-4 w-4 text-primary" />
              <span>Status da Loja e Conexão</span>
            </div>

            <Card className="bg-card/50 border-border/60">
              <CardHeader className="p-4 pb-2">
                <CardDescription className="text-[11px]">Status da Conexão</CardDescription>
                <CardTitle className="text-xs font-semibold flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 pt-1">
                  <Activity className="h-4 w-4 animate-pulse" />
                  Sincronização Ativa
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-1 text-[11px] text-muted-foreground">
                Seus clientes e mensagens estão sincronizados com sua loja em tempo real.
              </CardContent>
            </Card>
          </section>

          {/* Section 2 & 3: Store AI Preferences & Widget Branding */}
          <StorePreferencesForm
            storeId={store.id}
            initialPreferences={preferences}
            isLoading={isLoadingPreferences}
            isSaving={putMutation.isPending || patchMutation.isPending}
            onSave={handleSavePreferences}
            onToggleWidget={handleToggleWidgetStatus}
            onLiveChange={(updated) => setLivePreferences({ ...updated, storeId: store.id })}
          />

        </div>

        {/* Right Side Navigational Aid / Topics Scrollspy (4 cols) */}
        <div className="hidden lg:block lg:col-span-4 sticky top-6 space-y-4">
          <Card className="border-border/60 shadow-xs bg-card/60 backdrop-blur-sm">
            <CardHeader className="p-4 pb-3 border-b border-border/40">
              <div className="flex items-center gap-2">
                <Sliders className="h-4 w-4 text-primary" />
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  Tópicos da Loja
                </span>
              </div>
              <CardDescription className="text-[11px]">
                Ir para qualquer seção nesta página
              </CardDescription>
            </CardHeader>

            <CardContent className="p-2 space-y-1">
              {TOPIC_SECTIONS.map((topic) => {
                const Icon = topic.icon;
                const isActive = activeSection === topic.id;

                return (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => scrollToSection(topic.id)}
                    className={cn(
                      'w-full flex items-start gap-3 p-2.5 rounded-lg text-left transition-all text-xs group cursor-pointer',
                      isActive
                        ? 'bg-primary/10 text-primary font-semibold border-l-2 border-primary shadow-xs'
                        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    )}
                  >
                    <Icon
                      className={cn(
                        'h-4 w-4 mt-0.5 shrink-0 transition-colors',
                        isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate leading-tight">{topic.label}</p>
                      <p className="text-[10px] text-muted-foreground line-clamp-1 mt-0.5 font-normal">
                        {topic.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Tips / Info Box */}
          <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 text-xs space-y-2 text-muted-foreground">
            <div className="flex items-center gap-2 text-foreground font-semibold text-xs">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span>Sincronização em Tempo Real</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              As alterações feitas na Persona de IA ou na Aparência do Widget são atualizadas em tempo real para os clientes que visitam sua loja virtual em questão de segundos.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Live Simulation Widget */}
      <WidgetPreview preferences={livePreferences} />
    </div>
  );
};

export default StoreDetailView;
